import React from "react";
import { Link } from "react-router-dom";
import { Printer } from "lucide-react";
import { PageHeader } from "@/components/common";
import { useMeta } from "@/hooks/useMeta";

import { RING_SIZES as SIZES } from "@/lib/ringSizes";

function Ruler({ mm, testId }: { mm: number; testId: string }) {
  return (
    <svg width={`${mm}mm`} height="12mm" viewBox={`0 0 ${mm} 12`} className="block" data-testid={testId}>
      <rect x="0" y="0" width={mm} height="12" fill="none" stroke="#111" strokeWidth="0.2" />
      {Array.from({ length: mm + 1 }, (_, i) => (
        <g key={i}>
          <line x1={i} y1="0" x2={i} y2={i % 10 === 0 ? 6 : i % 5 === 0 ? 4 : 2.5} stroke="#111" strokeWidth="0.15" />
          {i % 10 === 0 && i > 0 && i < mm && <text x={i} y="9.5" fontSize="2.6" textAnchor="middle" fill="#111">{i}</text>}
        </g>
      ))}
    </svg>
  );
}

function Circles() {
  return (
    <div className="flex flex-wrap gap-x-[6mm] gap-y-[5mm] items-end" data-testid="ring-sizer-circles">
      {SIZES.map((s) => (
        <div key={s.us} className="flex flex-col items-center">
          <span className="rounded-full border border-neutral-900" style={{ width: `${s.d}mm`, height: `${s.d}mm`, borderWidth: "0.25mm" }} />
          <span className="text-[10px] mt-1">US {s.us}</span>
        </div>
      ))}
    </div>
  );
}

function Conversion() {
  return (
    <table className="w-full text-sm border border-neutral-200 bg-white" data-testid="ring-size-conversion-table">
      <thead className="bg-neutral-50 text-left"><tr>{["US / Canada", "UK / Australia", "Europe / ISO", "Inside diameter (mm)"].map((h) => <th key={h} className="px-4 py-2.5 eyebrow text-[10px] text-neutral-500 font-normal">{h}</th>)}</tr></thead>
      <tbody className="divide-y divide-neutral-100">
        {SIZES.map((s) => <tr key={s.us}><td className="px-4 py-2">{s.us}</td><td className="px-4 py-2">{s.uk}</td><td className="px-4 py-2">{s.eu}</td><td className="px-4 py-2">{s.d}</td></tr>)}
      </tbody>
    </table>
  );
}

export default function RingSizer() {
  useMeta("Printable Ring Sizer & International Size Conversion | Somnio.Co", "Print our ring sizer at home and convert ring sizes between US, UK, Australian and European systems.");
  return (
    <div data-testid="ring-sizer-page">
      <PageHeader eyebrow="Ring Size Guide" title="Printable Ring Sizer" intro='Print this page at 100% — set page scaling to "none" or "actual size" in your print settings. Then check the calibration line below measures exactly 50 mm with a ruler before you start.' />
      <div className="max-w-[1400px] mx-auto px-5 md:px-12 space-y-14 pb-10">
        <div className="flex flex-wrap gap-4 items-center print:hidden">
          <button onClick={() => window.print()} className="btn-ink px-8 py-3.5 text-[11px] tracking-[0.2em] uppercase flex items-center gap-2" data-testid="ring-sizer-print-button"><Printer size={14} />Print / save as PDF</button>
          <Link to="/education/ring-size-guide" className="text-xs underline">Back to the Ring Size Guide</Link>
        </div>
        <section className="space-y-3"><p className="eyebrow">Calibration — this line should measure 50 mm</p><Ruler mm={50} testId="ring-sizer-calibration" /></section>
        <section className="space-y-4">
          <h2 className="font-display text-2xl">Method 1 · Use a ring that fits</h2>
          <p className="text-sm text-neutral-600 max-w-2xl">Place a ring that fits the intended finger over the circles. The right size is the circle whose outline sits just inside the ring's inner edge. If it falls between two circles, choose the larger size.</p>
          <Circles />
        </section>
        <section className="space-y-4 break-inside-avoid">
          <h2 className="font-display text-2xl">Method 2 · Measure your finger</h2>
          <p className="text-sm text-neutral-600 max-w-2xl">Cut out the strip, wrap it snugly around the base of your finger and note the millimetre mark where it overlaps. That's your finger's circumference — find the closest number in the Europe / ISO column below.</p>
          <Ruler mm={90} testId="ring-sizer-strip" />
        </section>
        <section id="conversion" className="space-y-4 scroll-mt-48 break-before-page">
          <h2 className="font-display text-2xl">International Ring Size Conversion</h2>
          <p className="text-sm text-neutral-600 max-w-2xl">Our rings are listed in US sizes. Use this chart to find your equivalent size. Conversions are approximate — if you're unsure, order a free <Link to="/pages/customer-care#ring-sizing" className="underline">plastic ring sizer</Link>.</p>
          <Conversion />
        </section>
      </div>
    </div>
  );
}
